import { EyeIcon } from '@heroicons/react/24/solid';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CardResponseDto,
  OpportunityCardView,
  ReadyRosteredCardView,
  TrackingResponseDto,
} from '../../types/types';
import { formatPurseText } from '../../utils/opportunityAndReadyroster';
import { formatSportName } from '../../utils/utilMethods';

interface CardsCarouselSectionProps {
  cardStats: CardResponseDto | null;
  selectedCard: OpportunityCardView | ReadyRosteredCardView | null;
  onCardSelect: (card: OpportunityCardView | ReadyRosteredCardView) => void;
  selectedCardStatsMap: Record<string, TrackingResponseDto>;
  cardRangeMap: Record<string, '7days' | 'month' | 'all'>;
  onCardRangeChange: (
    card: OpportunityCardView | ReadyRosteredCardView,
    range: '7days' | 'month' | 'all'
  ) => void;
  currentSlide: number;
  setCurrentSlide: (slide: number) => void;
  cardsPerSlide: number;
}

const getCardId = (card: OpportunityCardView | ReadyRosteredCardView) =>
  'cardId' in card ? card.cardId : card.id.toString();

const getCardSport = (card: OpportunityCardView | ReadyRosteredCardView) =>
  card.formData?.details?.sport ||
  card.cardResponseDto?.sport ||
  '—';

const getCardAmount = (card: OpportunityCardView | ReadyRosteredCardView) =>
  card.formData?.core?.baseAmount ??
  card.formData?.baseAmount ??
  card.cardResponseDto?.purse;

const getCardCurrency = (card: OpportunityCardView | ReadyRosteredCardView) =>
  card.formData?.core?.currency ??
  card.formData?.currency ??
  card.cardResponseDto?.currency ??
  '—';

const CardDetailView: React.FC<{
  card: OpportunityCardView | ReadyRosteredCardView;
  isSelected: boolean;
  onClick: () => void;
  cardRange: '7days' | 'month' | 'all';
  onRangeChange: (range: '7days' | 'month' | 'all') => void;
  cardStats?: TrackingResponseDto;
}> = ({ card, isSelected, onClick, cardRange, onRangeChange, cardStats }) => {
  const { t } = useTranslation('dashboard');
  const totalViews = cardStats?.totalViews ?? card.views ?? 0;
  const [displayedViews, setDisplayedViews] = useState<number>(0);

  const sport = getCardSport(card);
  const amount = getCardAmount(card);
  const currency = getCardCurrency(card);

  useEffect(() => {
    let count = 0;
    const target = totalViews;
    const step = Math.ceil(target / 30);

    setDisplayedViews(0);

    const interval = setInterval(() => {
      count += step;
      if (count >= target) {
        count = target;
        clearInterval(interval);
      }
      setDisplayedViews(count);
    }, 20);

    return () => clearInterval(interval);
  }, [totalViews, cardRange]);

  const showAmount = amount !== undefined && amount !== null;

  return (
    <div
      onClick={onClick}
      className={`max-h-[110px] max-w-60 bg-white p-2 shadow-md border cursor-pointer rounded-md ${
        isSelected
          ? 'border-blue-400 bg-blue-50 shadow-md scale-102'
          : 'border-slate-200 hover:shadow-xl hover:scale-102'
      }`}
    >
      <div className="flex justify-between items-center">
        <div className="flex flex-col gap-1">
          <div className="relative group/sport max-w-[110px]">
            <span className="bg-pink-100 text-pink-600 text-xs px-2 py-0.5 font-medium truncate block rounded-md">
              {formatSportName(sport)}
            </span>
            <div className="absolute left-0 top-full mt-1 hidden group-hover/sport:block bg-slate-800 text-white text-xs px-2 py-1 z-10 shadow-lg max-w-[200px] truncate rounded-md">
              {formatSportName(sport)}
            </div>
          </div>

          {showAmount && (
            <div className="relative group/purse max-w-[120px]">
              <span className="bg-blue-100 text-blue-600 text-xs px-2 py-0.5 font-medium truncate block max-w-full rounded-md">
                {formatPurseText(`${amount}`)} {currency}
              </span>

              <div className="absolute left-0 top-full mt-1 hidden group-hover/purse:block bg-slate-800 text-white text-xs px-2 py-1 z-10 shadow-lg max-w-[200px] truncate rounded-md">
                {formatPurseText(`${amount}`)} {currency}
              </div>
            </div>
          )}
        </div>

        <div className="text-center ml-4 min-w-[64px]">
          <div className="text-xs mb-1 text-slate-500 font-medium">
            {t('views')}
          </div>
          <div className="text-3xl font-bold text-slate-800">
            {displayedViews}
          </div>
        </div>
      </div>

      <div className="pt-1 mt-3">
        <div className="flex justify-center gap-2 text-[10px] text-slate-600">
          {(['7days', 'month', 'all'] as const).map((rangeVal) => (
            <label key={rangeVal} className="flex items-center gap-1 cursor-pointer">
              <input
                type="radio"
                value={rangeVal}
                checked={cardRange === rangeVal}
                onChange={() => onRangeChange(rangeVal)}
                className="w-3 h-3"
              />
              <span>
                {t(
                  rangeVal === '7days'
                    ? 'seven_days'
                    : rangeVal === 'month'
                      ? 'one_month'
                      : 'all'
                )}
              </span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
};

export const CardsCarouselSection: React.FC<CardsCarouselSectionProps> = ({
  cardStats,
  selectedCard,
  onCardSelect,
  selectedCardStatsMap,
  cardRangeMap,
  onCardRangeChange,
  currentSlide,
  setCurrentSlide,
  cardsPerSlide,
}) => {
  const { t } = useTranslation('dashboard');

  if (!cardStats || !cardStats.cardViews || cardStats.cardViews.length === 0) {
    return (
      <div className="col-span-9">
        <div className="flex flex-col items-center justify-center py-16 text-center bg-slate-50 min-h-[200px] rounded-md">
          <div className="w-16 h-16 bg-slate-100 rounded-md flex items-center justify-center mb-4">
            <EyeIcon className="w-8 h-8 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-700 mb-2">
            {t('no_cards_available')}
          </h3>
          <p className="text-slate-500 max-w-md">{t('no_cards_description')}</p>
        </div>
      </div>
    );
  }

  const totalSlides = Math.ceil(cardStats.cardViews.length / cardsPerSlide);

  const goToSlide = (slideIndex: number) => {
    setCurrentSlide(slideIndex);
  };

  return (
    <div className="col-span-9">
      <div className="relative">
        <div className="overflow-hidden">
          <div
            className="flex gap-2 transition-transform duration-300 ease-in-out"
            style={{
              transform: `translateX(-${currentSlide * (100 / totalSlides)}%)`,
              width: `${totalSlides * 100}%`,
            }}
          >
            {cardStats.cardViews.map((cv, idx) => {
              const cardId = getCardId(cv);

              return (
                <div
                  key={`card-${cardId}-${idx}`}
                  className="flex-shrink-0"
                  style={{ width: `${100 / (totalSlides * cardsPerSlide)}%` }}
                >
                  <CardDetailView
                    card={cv}
                    isSelected={
                      !!selectedCard &&
                      ('cardId' in cv
                        ? cv.cardId ===
                          ('cardId' in selectedCard ? selectedCard.cardId : '')
                        : cv.id === ('id' in selectedCard ? selectedCard.id : 0))
                    }
                    onClick={() => onCardSelect(cv)}
                    cardRange={cardRangeMap[cardId] || '7days'}
                    onRangeChange={(range) => onCardRangeChange(cv, range)}
                    cardStats={selectedCardStatsMap[cardId]}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {totalSlides > 1 && (
          <div className="flex justify-center gap-1 mt-3">
            {Array.from({ length: totalSlides }).map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-2 h-2 rounded-full transition-all duration-200 ${
                  currentSlide === index
                    ? 'bg-blue-500'
                    : 'bg-slate-300 hover:bg-slate-400'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};